import type { CrashMonitorConfig, HeartbeatData } from '../types';

import { logger as moduleLogger } from '@/crashReports/utils/logger';
const logger = moduleLogger.createSubLogger('CrashMonitor');

export class CrashMonitor {
  protected worker: Worker | null = null;
  protected heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  protected visibilityChangeHandler: (() => void) | null = null;
  protected pageHideHandler: (() => void) | null = null;
  protected config: CrashMonitorConfig;
  protected sessionId: string;
  protected isInitialized = false;

  constructor(config: Partial<CrashMonitorConfig> = {}) {
    this.config = {
      heartbeatInterval: 1000, // 1秒心跳间隔
      crashTimeout: 5000, // 5秒崩溃超时
      reportEndpoint: '/client/error/reports',
      ...config,
    };

    // 生成会话ID
    this.sessionId = this.generateSessionId();
  }

  /**
   * 启动崩溃监测
   */
  async start(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('CrashMonitor is already started');
      return;
    }

    try {
      // 创建WebWorker
      if (process.env.NODE_ENV === 'production') {
        this.worker = new Worker('/crashReport.worker.js');
      } else {
        this.worker = new Worker(
          new URL('../worker/crashReport.worker.ts', import.meta.url),
          {
            type: 'module',
          }
        );
      }

      // 监听Worker消息
      this.worker.addEventListener(
        'message',
        this.handleWorkerMessage.bind(this)
      );

      // 初始化Worker
      await this.initWorker();

      // 开始心跳
      this.startHeartbeat();

      this.isInitialized = true;
      logger.log('CrashMonitor started successfully');
    } catch (error) {
      logger.error('Failed to start CrashMonitor:', error);
      throw error;
    }
  }

  /**
   * 停止崩溃监测
   */
  stop(): void {
    if (!this.isInitialized) {
      return;
    }

    // 停止心跳
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // 移除页面可见性监听器
    this.removeVisibilityListeners();

    // 停止Worker
    if (this.worker) {
      this.worker.postMessage({ type: 'stop' });
      this.worker.terminate();
      this.worker = null;
    }

    this.isInitialized = false;
    logger.log('CrashMonitor stopped');
  }

  /**
   * 手动发送心跳（可选，通常由定时器自动发送）
   */
  sendHeartbeat(): void {
    if (!this.isInitialized || !this.worker) {
      return;
    }

    this.worker.postMessage({
      type: 'heartbeat',
      data: this.getHeartbeatData(),
    });
  }

  /**
   * 获取当前会话ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<CrashMonitorConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // 如果Worker已启动，重新初始化
    if (this.isInitialized && this.worker) {
      this.initWorker();
    }
  }

  private async initWorker(): Promise<void> {
    if (!this.worker) return;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Worker initialization timeout'));
      }, 5000);

      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'initialized') {
          clearTimeout(timeout);
          this.worker!.removeEventListener('message', handleMessage);
          resolve();
        }
      };

      this.worker!.addEventListener('message', handleMessage);

      // 发送初始化消息
      this.worker!.postMessage({
        type: 'init',
        config: this.config,
      });
    });
  }

  private handleWorkerMessage(event: MessageEvent): void {
    const { type } = event.data;

    switch (type) {
      case 'initialized':
        logger.log('Worker initialized');
        break;
      default:
        logger.log('Worker message:', event.data);
    }
  }

  getHeartbeatData(): HeartbeatData {
    return {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
    };
  }

  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // 立即发送一次心跳
    this.sendHeartbeat();

    // 设置定时心跳
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.config.heartbeatInterval);

    // 添加页面可见性监听器
    this.addVisibilityListeners();
  }

  /**
   * 添加页面可见性监听器
   */
  private addVisibilityListeners(): void {
    if (typeof document === 'undefined') return;

    // 页面可见性变化监听
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // 页面隐藏时，立即发送心跳并通知Worker
        this.sendHeartbeat();
        if (this.worker) {
          this.worker.postMessage({
            type: 'pageHidden',
            timestamp: Date.now(),
          });
        }
        logger.log('📱 页面隐藏，暂停心跳监测');
      } else {
        // 页面显示时，立即发送心跳并恢复监测
        this.sendHeartbeat();
        if (this.worker) {
          this.worker.postMessage({
            type: 'pageVisible',
            timestamp: Date.now(),
          });
        }
        logger.log('📱 页面显示，恢复心跳监测');
      }
    };

    // 页面卸载监听
    this.pageHideHandler = () => {
      // 页面卸载时发送最后一次心跳
      this.sendHeartbeat();
      logger.log('📱 页面卸载，发送最后心跳');
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  /**
   * 移除页面可见性监听器
   */
  private removeVisibilityListeners(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler
      );
      this.visibilityChangeHandler = null;
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
